import type { Dispatch, SetStateAction } from "react";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components/macro";
import useSWR from "swr";

import {
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../layout/portalPageLayout";
import type { SEODetail, WithPagination } from "../../../types/types";

import { ErrorPlaceholder } from "../../../components/Error";
import type { ErrorWithStatus } from "../../../util/util";
import {
  PIM_PRODUCT_DETAILS_PORTFOLIO_TAB,
  useStoreState,
} from "../../../util/util";

import { Tabs } from "@reach/tabs";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { helmetJsonLdProp } from "react-schemaorg";
import type { Product } from "schema-dts";
import { NumberParam, useQueryParams } from "use-query-params";
import { Auth } from "../../../components/Auth";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { Modal } from "../../../components/Modal/Modal";
import { SEOHelmet } from "../../../components/SEOHelmet/SEOHelmet";
import { endpoints } from "../../../endpoints";
import type {
  AttributeSchema,
  AttributeTemplateSchema,
  PIMProduct,
  ProductListPriceSchema,
} from "../../../types/types.PIM";
import { useRoutePath } from "../../../util/Routing";
import type { ProductMutate } from "../../SharedPages/ProductDetailPage/ProductDetailPage";
import { ProductTabs } from "../../SharedPages/ProductDetailPage/util";
import { ProductDetailsTransactionsSection } from "./ProductDetailsTransactionsButtons";

const ProductHeader = styled(PageHeader)`
  align-items: flex-start;
  justify-content: flex-start;
`;

const ProductHeaderImage = styled.img`
  min-width: 100px;
  max-width: 200px;
  max-height: 150px;
  margin-right: 15px;
  border: 1px solid ${({ theme }) => theme.primaryBorder};
  border-radius: 8px;
  padding: 1px;
  align-self: center;
  cursor: pointer;
`;

const ProductHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  padding: 0;
  h1 {
    margin: 0px 5px 2px 0;
  }
`;

const ModalWrapper = styled.div`
  padding: 20px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface IProductDetails {
  product: PIMProduct | undefined;
  mutateProduct: ProductMutate;
  error: ErrorWithStatus | undefined;
  updateListHeight?: Dispatch<SetStateAction<number | undefined>>;
  setbackToProductList?: Dispatch<SetStateAction<boolean>>;
  can_set_tab_query?: boolean;
}
/**
 * Portfolio Product detail page
 */
export function PIMProductDetailsPortfolio({
  product,
  mutateProduct,
  error,
  updateListHeight,
  setbackToProductList,
  can_set_tab_query = true,
}: IProductDetails) {
  const { hasPermission, roleIsGuest, roleIsSomeKindOfBuyer } =
    useContext(Auth);

  const { t } = useTranslation();
  const { storefront_metadata, tenant_id, edition } = useStoreState();
  const { pathname } = useLocation();
  const history = useHistory();
  const { storePath } = useRoutePath();
  const portfolioView = sessionStorage.getItem("portfolioView");
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  const [fullSizeProductImage, setFullSizeProductImage] = useState(false);
  const [query, setQuery] = useQueryParams({ tab: NumberParam });
  const [currentTab, setCurrentTab] = useState(0);

  const { data: product_list_prices } = useSWR<
    WithPagination<{ data: ProductListPriceSchema[] }>
  >(
    product?.id
      ? endpoints.v2_tenants_tenant_id_products_product_id_list_prices(
          tenant_id,
          product.id
        )
      : null
  );

  useEffect(() => {
    // we don't know the length of the tabs that the buyer will see, so when the
    // product changes reset to the first tab, because there will always be 1.
    const tab =
      query?.tab ??
      (localStorage.getItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB)
        ? Number(localStorage.getItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB))
        : 0);
    setCurrentTab(tab);
  }, [query]);

  const changeTab = (index: number) => {
    if (updateListHeight) {
      updateListHeight(index);
    }
    setCurrentTab(index);
    if (can_set_tab_query) {
      setQuery({ tab: index });
    } else {
      localStorage.setItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB, `${index}`);
    }
  };

  useEffect(() => {
    if (roleIsSomeKindOfBuyer) {
      if (portfolioView === "modern" || portfolioView === "classic") {
        const basePath = `${storePath}/admin/pim/products`;
        const orderId = pathname.split("/").pop();
        history.replace(`${basePath}/${orderId}`);
      }
    }
  }, [history, roleIsSomeKindOfBuyer, portfolioView, storePath, pathname]);

  if (!product) {
    // Product data is still loading.
    return <DelayedSpinner />;
  }

  const productCoverImage = product.cover_image?.signed_url;

  const seoDetails = (): SEODetail => {
    const seoGroupSummary = product.product_schema.groups.find(
      (group) => group.name.toLowerCase() === "seo"
    );

    const seoDataType: Record<string, string> = {};

    seoGroupSummary?.attributes?.forEach(
      ({ object_type, values }) =>
        (seoDataType[object_type] = (values?.[0]?.value as string) ?? "")
    );

    return {
      meta_description: seoDataType?.seo_meta_description ?? null,
      meta_keywords: seoDataType?.seo_meta_keywords ?? null,
      meta_title: seoDataType?.seo_meta_title ?? null,
      page_title: seoDataType?.seo_page_title ?? null,
      og_title: seoDataType?.seo_og_title ?? null,
      og_type: seoDataType?.seo_og_type ?? null,
      og_image: seoDataType?.seo_og_image ?? null,
      og_description: seoDataType?.seo_og_description ?? null,
      og_locale: seoDataType?.seo_og_locale ?? null,
      og_url: seoDataType?.seo_og_url ?? null,
    };
  };

  const get_default_tag_name = () => {
    return (
      product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          (attr) =>
            attr.object_type === "product_name" &&
            !attr.is_restricted &&
            !attr.is_not_applicable
        ) ?? null
    );
  };

  const get_default_tag_description = () => {
    return (
      product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          (attr) =>
            attr.object_type === "product_description" &&
            !attr.is_not_applicable &&
            !attr.is_restricted
        ) ?? null
    );
  };

  const get_property_value = (attribute: AttributeSchema | null) => {
    const attribute_value = product.product_schema.groups
      .flatMap(({ attributes }) => attributes)
      .find(
        (attr) =>
          attr.id === attribute?.id &&
          !attribute?.is_not_applicable &&
          !attribute?.is_restricted
      );
    if (attribute_value) {
      if (
        attribute_value.input_type === "multi_select" &&
        attribute_value.values?.length
      ) {
        return attribute_value.values
          .map((val) => val.value as string)
          .join(", ");
      }
      const val = attribute_value.values?.[0]?.value;
      switch (typeof val) {
        case "string":
          return val as string;
        case "boolean":
          return val ? "Yes" : "No";
        default:
          return "";
      }
    }
    return "";
  };

  const get_product_schema = (
    schema_tag: AttributeTemplateSchema["schema_tags"]
  ) => {
    return helmetJsonLdProp<Product>({
      "@context": "https://schema.org",
      "@type": "Product",
      name: get_property_value(schema_tag?.tag_name_attribute ?? null) ?? "",
      description:
        get_property_value(schema_tag?.tag_description_attribute ?? null) ?? "",
      url: `${window.location.origin}${storePath}/product/${product.slug}`,
    });
  };

  if (error) {
    return (
      <ErrorPlaceholder
        message={error.message ?? t("There was an error loading the product.")}
      />
    );
  }

  return (
    <PageWrapper style={{ position: "relative" }}>
      {seoDetails() && (
        <SEOHelmet
          seo={seoDetails()}
          extraProps={{
            slug: product.name,
            image_url: product.image_url,
          }}
        />
      )}
      <ProductHeader>
        {productCoverImage && (
          <ProductHeaderImage
            src={productCoverImage}
            alt={product.name}
            onClick={() => setShowProductImageModal(true)}
          />
        )}
        <ProductHeaderContainer>
          <PageTitle>{product.name}</PageTitle>
          {product.product_schema.show_schema_tag && edition !== "pim" ? (
            <Helmet
              script={[
                get_product_schema(
                  product.product_schema.schema_tags ?? {
                    tenant_id,
                    tag_name_attribute: get_default_tag_name(),
                    tag_description_attribute: get_default_tag_description(),
                  }
                ),
              ]}
            >
              <meta property="og:title" content={product.name} />
              {product.image_url && (
                <meta property="og:image" content={product.image_url} />
              )}
              <meta
                property="og:description"
                content={`Check out ${product.name} ${
                  product.produced_by ? `by ${product.produced_by}` : ``
                } 
    on the ${storefront_metadata.tenant_name} portfolio`}
              />
            </Helmet>
          ) : null}
          <ProductDetailsTransactionsSection
            product={product}
            setbackToProductList={setbackToProductList}
          />
        </ProductHeaderContainer>
      </ProductHeader>

      <FullWidthHorizontalSeparator margin={"56px 0 0"} />
      {product && (
        <Tabs
          onChange={changeTab}
          style={{ marginTop: "8px" }}
          index={currentTab}
          key={product.id}
          defaultIndex={0}
        >
          {ProductTabs({
            product,
            t,
            refreshKey: currentTab,
            mutate: mutateProduct,
            hasPermission,
            replaceProductId: () => Promise.resolve(),
            isPortfolio: true,
            roleIsGuest,
            roleIsBuyer: roleIsSomeKindOfBuyer,
            has_list_prices: (product_list_prices?.data.length ?? 0) > 0,
            isShowTableWithBulkAction: true,
            isShowGridAssetWithBulkAction: true,
          })}
        </Tabs>
      )}

      {showProductImageModal && productCoverImage && (
        <Modal
          show={showProductImageModal}
          overlay={true}
          closeModal={() => setShowProductImageModal(false)}
          modalWidth="100%"
        >
          <ModalWrapper>
            <img
              src={productCoverImage}
              alt={product.name}
              style={{
                maxWidth: fullSizeProductImage ? "auto" : "100%",
                maxHeight: fullSizeProductImage ? "auto" : "100%",
                cursor: fullSizeProductImage ? "zoom-out" : "zoom-in",
              }}
              onClick={() => setFullSizeProductImage((current) => !current)}
            />
          </ModalWrapper>
        </Modal>
      )}
    </PageWrapper>
  );
}
